import React from "react";
import {
  Grid,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Checkbox,
  Tabs,
  Tab,
  Divider,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AuthContext from "../../../contexts/AuthContext";
import LanguageSwitcher from "../../../utilities/LanguageSwitcher";
import BrowseEvents from "./BrowseEvents";
import { login } from "../../../utilities/authFlow";
import { useNavigate } from "react-router-dom";

const Login = ({ setPage, config, orgId, defaultTab = "login" }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setSignedIn, email, setEmail, appStyle, appConfig } =
    React.useContext(AuthContext);
  const navigate = useNavigate();
  // const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [passwordVisibility, setPasswordVisibility] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [error, setError] = React.useState("");
  const [activeTab, setActiveTab] = React.useState(defaultTab);

  React.useEffect(() => {
    const reEmail = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.email`,
    );
    if (reEmail) {
      setRememberMe(true);
      setEmail(reEmail);
    }
    try {
      const flag = localStorage.getItem("tnft.returnToBrowse");
      if (flag === "1") {
        setActiveTab("browse");
        localStorage.removeItem("tnft.returnToBrowse");
      }
    } catch {}
  }, []);

  const handleLogin = async () => {
    const result = await login(email, password);
    if (result.success) {
      setSignedIn(true);
    } else {
      setError(result.error);
      setSignedIn(false);
    }
  };

  const handleRememberMe = () => {
    if (!rememberMe) {
      setRememberMe(true);
      localStorage.setItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.email`,
        email,
      );
    } else {
      setRememberMe(false);
      localStorage.removeItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.email`,
      );
    }
  };

  return (
    <Grid container spacing={0} justifyContent="center" id="login-container">
      <Grid
        item
        xs={12}
        md={7}
        lg={7}
        style={{ display: "flex", justifyContent: "center" }}
        sx={{ mb: 3 }}
      >
        {/* <form style={{ display: "flex", justifyContent: "center" }}> */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: { xs: "100vw", sm: 520, md: 420 },
            mx: "auto",
          }}
        >
          <img
            src={"https://d1unewf2oslns8.cloudfront.net/juice-bottle.webp"}
            alt="Juice bottle"
            style={{ width: "100%", height: "auto", display: "block" }}
          />

          <Box
            id="bottle-box"
            sx={{
              position: "absolute",
              top: "28%",
              left: "12%",
              width: "76%",
              height: "63%",
              backgroundColor: "#ffffff",
              borderRadius: "68px 28px 18px 18px",
              borderTopLeftRadius: "158px",
              borderTopRightRadius: "158px",
              padding: 2,
              paddingBottom: 6,
              // paddingTop: 3, // removed extra top padding
              overflow: "hidden",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box
              id="bottle-flex-1"
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                justifyContent: "flex-start",
                gap: 2,
                pt: 2,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={"https://d1unewf2oslns8.cloudfront.net/main-squeeze-logo.png"}
                  alt="Natalie's logo"
                  style={{
                    height: 200,
                    maxWidth: "100%",
                    display: "block",
                    marginTop: -20,
                    marginBottom: -20,
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  variant="body2"
                  align="center"
                  style={{ fontWeight: 600 }}
                >
                  {t("auth.login.title")}
                </Typography>
              </Box>

              {error && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography
                    variant="subtitle2"
                    style={{ color: theme.palette.primary.main }}
                  >
                    {error}
                  </Typography>
                </Box>
              )}

              <TextField
                autoFocus
                label={t("email.textbox.label")}
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={(e) => {
                  const trimmedEmail = e.target.value.trim();
                  setEmail(trimmedEmail);
                }}
                placeholder={t("email.textbox.placeholder")}
                fullWidth
                inputProps={{ autoCapitalize: "none" }}
                autoComplete="off"
              />

              <TextField
                margin="dense"
                label={t("password.textbox.label")}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("password.textbox.placeholder")}
                fullWidth
                type={passwordVisibility ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      onClick={() => setPasswordVisibility(!passwordVisibility)}
                    >
                      {passwordVisibility ? (
                        <VisibilityOff style={{ cursor: "pointer" }} />
                      ) : (
                        <Visibility style={{ cursor: "pointer" }} />
                      )}
                    </InputAdornment>
                  ),
                }}
                autoComplete="off"
              />

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={rememberMe}
                  onChange={handleRememberMe}
                  sx={{
                    "&.Mui-checked": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
                <Typography variant="subtitle2">
                  {t("auth.rememberme.label")}
                </Typography>
              </Box>

              <Button
                variant="contained"
                sx={{ width: "100%", height: 36 }}
                onClick={handleLogin}
              >
                {t("auth.signin.btn")}
              </Button>

              <Button
                variant="outlined"
                sx={{
                  width: "100%",
                  height: 36,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                }}
                onClick={() => setPage("signup")}
              >
                {t("auth.createaccount.btn")}
              </Button>

              <Button
                sx={{
                  width: "100%",
                  height: 30,
                  color: theme.palette.primary.main,
                }}
                onClick={() => setPage("forgot")}
              >
                {t("auth.reset.btn")}
              </Button>

              <Typography
                variant="subtitle2"
                align="center"
                sx={{ color: "#353535" }}
              >
                {t("auth.havecode")}{" "}
                <span
                  onClick={() => setPage("confirm")}
                  style={{
                    color: theme.palette.primary.main,
                    cursor: "pointer",
                  }}
                >
                  {t("here")}
                </span>
                .
              </Typography>

              <Typography
                variant="subtitle2"
                align="center"
                sx={{ px: 1, fontSize: "12px" }}
              >
                {t("auth.havingtrouble.txt")}{" "}
                <span
                  onClick={() => setPage("troubleshoot")}
                  style={{
                    color: theme.palette.primary.main,
                    cursor: "pointer",
                  }}
                >
                  {t("auth.havingtrouble.link")}
                </span>
              </Typography>
            </Box>
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: "22px",
                backgroundColor: theme.palette.primary.main,
                borderBottomLeftRadius: "18px",
                borderBottomRightRadius: "18px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography
                align="center"
                variant="caption"
                sx={{ color: "white" }}
              >
                Love at First Sip
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* </form> */}
      </Grid>
      <Grid item xs={12}>
        <Grid container direction="column" spacing={2}>
          <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "100vw", sm: 520, md: 420 },
                mx: "auto",
              }}
            >
              <img
                src={"https://d1unewf2oslns8.cloudfront.net/natalies-xoxo.png"}
                alt="natalies-xoxo"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </Box>
          </Grid>
          <Grid item>
            <Typography align="center" style={{ color: "#353535" }}>
              {t("footer.copyright")}
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              align="center"
              style={{ color: "#353535", cursor: "pointer" }}
              onClick={() => window.open(appConfig?.privacyPolicy, "_blank")}
            >
              {t("privacypolicy.link")}
            </Typography>
          </Grid>
          <Grid item style={{ marginBottom: 50 }}>
            <LanguageSwitcher />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Login;
